import React, { useState, useEffect } from 'react';
import { CreditCard, TrendingUp, TrendingDown, DollarSign, Calendar, Download, IndianRupee } from 'lucide-react';
import TransactionFilters from './TransactionFilters';
import TransactionTable from './TransactionTable';
import TransactionDetailsModal from './TransactionDetailsModal';
import { useTransactions } from '../context/TransactionsContext';
import Pagination, { usePagination } from '../shared/Pagination';

function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button className="text-stone-400 hover:text-stone-700 text-2xl leading-none" onClick={onClose}>&times;</button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-auto">{children}</div>
        {footer && <div className="px-6 py-4 border-t bg-stone-50 flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
}

/* ─── Reusable stat card ─────────────────────────────────────────────────── */
function StatCard({ label, value, icon: Icon, iconBg, iconColor, trend, trendValue }) {
  const isUp = trend === 'up';
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-4 flex flex-col justify-between">
      {/* Top row */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
        <div className={`w-8 h-8 md:w-9 md:h-9 ${iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
      </div>
      {/* Value */}
      <p className="text-[1.15rem] md:text-xl font-bold text-gray-900 mt-1.5 truncate">{value}</p>
      {/* Trend */}
      <div className="flex items-center gap-1 mt-2">
        {isUp
          ? <TrendingUp className="w-3 h-3 text-green-500 flex-shrink-0" />
          : <TrendingDown className="w-3 h-3 text-red-500 flex-shrink-0" />}
        <span className={`text-[11px] font-semibold ${isUp ? 'text-green-600' : 'text-red-600'}`}>{trendValue}</span>
        <span className="text-[11px] text-gray-400">vs last month</span>
      </div>
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────────────────── */
const TransactionsPage = () => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [exporting, setExporting] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [amountRange, setAmountRange] = useState({ min: '', max: '' });
 

  const { transactions, loading, fetchTransactions } = useTransactions();

  const analyticsData = React.useMemo(() => {
    if (!transactions?.length) return { totalRevenue: 0, totalTransactions: 0, successRate: 0, averageTransaction: 0, revenueGrowth: 0, transactionGrowth: 0 };
    const total = transactions.length;
    const successful = transactions.filter(t => t.status === 'success' || t.status === 'paid');
    const revenue = successful.reduce((s, t) => s + (t.amount || 0), 0);
    const now = new Date();
    const last7 = transactions.filter(t => (now - new Date(t.date)) / 86400000 <= 7);
    const prev7 = transactions.filter(t => { const d = (now - new Date(t.date)) / 86400000; return d > 7 && d <= 14; });
    const l7r = last7.reduce((s, t) => s + (t.amount || 0), 0);
    const p7r = prev7.reduce((s, t) => s + (t.amount || 0), 0);
    return {
      totalRevenue: revenue,
      totalTransactions: total,
      successRate: (successful.length / total) * 100,
      averageTransaction: successful.length ? revenue / successful.length : 0,
      revenueGrowth: p7r > 0 ? ((l7r - p7r) / p7r) * 100 : 0,
      transactionGrowth: prev7.length > 0 ? ((last7.length - prev7.length) / prev7.length) * 100 : 0,
    };
  }, [transactions]);

  useEffect(() => { fetchTransactions(); }, []);
  // useEffect(() => { setTotalPages(Math.ceil(transactions.length / 10)); }, [transactions]);

  const handleViewDetails = (t) => { setSelectedTransaction(t); setShowDetailsModal(true); };
  const handleDownloadReceipt = (t) => console.log('Receipt:', t.transactionId);
  const handleProcessRefund = (t) => console.log('Refund:', t.transactionId);

const filteredTransactions = transactions
  .filter((t) => {
    const s = searchTerm.toLowerCase();
    const matchSearch =
  !s ||
  t.transactionId?.toLowerCase().includes(s) ||
  t.orderId?.toLowerCase().includes(s) ||              // ✅ add this
  t.order?.id?.toLowerCase().includes(s) ||            // ✅ fallback if nested
  t.customer?.name?.toLowerCase().includes(s) ||
  t.customer?.email?.toLowerCase().includes(s);

    const matchStatus = !statusFilter || t.status === statusFilter;

    const matchDate = (() => {
  const txnDate = new Date(t.date);

  const startOk =
    !dateRange.start || txnDate >= new Date(dateRange.start);

  const endOk = (() => {
    if (!dateRange.end) return true;

    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999); // 🔥 include full day

    return txnDate <= endDate;
  })();

  return startOk && endOk;
})();

    const matchAmt =
      (!amountRange.min || t.amount >= +amountRange.min) &&
      (!amountRange.max || t.amount <= +amountRange.max);

    return matchSearch && matchStatus && matchDate && matchAmt;
  })
  .sort((a, b) => new Date(b.date) - new Date(a.date)); // 🔥 latest first
const {
  page,
  pageSize,
  setPage,
  setPageSize,
  totalPages,
  totalItems,
  paginated: paginatedTransactions,
} = usePagination(filteredTransactions, 10);
useEffect(() => { setPage(1); }, [searchTerm, statusFilter, dateRange, amountRange]);
  function triggerDownload(blob, filename) {
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: filename });
    document.body.appendChild(a); a.click(); a.remove();
  }
function downloadCSV(data) {
  const h = ["transactionId","status","date","amount","payment_method","customer_name","customer_email"];
  const formatCsvDate = (d) => {
    if (!d) return '';
    const dateObj = new Date(d);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };
  const rows = data.map(t => h.map(k => {
    const rowData = {
      transactionId: t.transactionId,
      status: t.status,
      date: formatCsvDate(t.date),
      amount: t.amount,
      payment_method: t.paymentMethod || t.payment_method || t.payment?.method || '',
      customer_name: t.customer?.name,
      customer_email: t.customer?.email
    };
    return `"${String(rowData[k] ?? '').replace(/"/g,'""')}"`;
  }).join(','));
  triggerDownload(new Blob([[h.join(','),...rows].join('\r\n')],{type:'text/csv;charset=utf-8;'}), 'Transactions.csv');
}
  function downloadJSON(data) {
    triggerDownload(new Blob([JSON.stringify(data,null,2)],{type:'application/json'}), 'Transactions.json');
  }
  const confirmExport = () => {
    setExporting(true);
    try { exportFormat === 'csv' ? downloadCSV(filteredTransactions) : downloadJSON(filteredTransactions); }
    finally { setExporting(false); setDownloadModalOpen(false); }
  };

//   const itemsPerPage = 10;

// const paginatedTransactions = React.useMemo(() => {
//   const startIndex = (currentPage - 1) * itemsPerPage;
//   return filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
// }, [filteredTransactions, currentPage]);
// useEffect(() => {
//   setTotalPages(Math.ceil(filteredTransactions.length / itemsPerPage));
// }, [filteredTransactions]);
  return (
    <main className="p-3 sm:p-4 md:p-5 max-w-7xl mx-auto" role="main">

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3 mb-5" aria-label="Transaction analytics">
        <StatCard label="Total Revenue"     value={`₹${analyticsData.totalRevenue.toLocaleString()}`}          icon={IndianRupee}  iconBg="bg-green-100"  iconColor="text-green-600"  trend="up"   trendValue={`+${analyticsData.revenueGrowth.toFixed(1)}%`} />
        <StatCard label="Transactions"      value={analyticsData.totalTransactions.toLocaleString()}            icon={CreditCard}  iconBg="bg-blue-100"   iconColor="text-blue-600"  trend="up"   trendValue={`+${analyticsData.transactionGrowth.toFixed(1)}%`} />
        <StatCard label="Success Rate"      value={`${analyticsData.successRate.toFixed(1)}%`}                  icon={TrendingUp}  iconBg="bg-teal-100"   iconColor="text-teal-600"  trend="up"   trendValue="+2.1%" />
        <StatCard label="Avg. Transaction"  value={`₹${analyticsData.averageTransaction.toFixed(0)}`}           icon={Calendar}    iconBg="bg-gray-100" iconColor="text-orange-600" trend="down" trendValue="-1.2%" />
      </section>

      {/* ── Filters ── */}
      <TransactionFilters
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        dateRange={dateRange} setDateRange={setDateRange}
        amountRange={amountRange} setAmountRange={setAmountRange}
        onExport={() => setDownloadModalOpen(true)}
      />

      {/* ── Table (md+) / Cards (mobile) ── */}
      <section className="mt-4">
       <div className="hidden md:block overflow-x-auto">
  <TransactionTable
    transactions={paginatedTransactions}
    loading={loading}
    onViewDetails={handleViewDetails}
    onDownloadReceipt={handleDownloadReceipt}
    onRefund={handleProcessRefund}
  />
  {totalPages > 1 && (
    <div className="border-t border-gray-100">
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  )}
</div>

        <div className="md:hidden space-y-3">
          {loading ? (
            <div className="p-4 bg-white rounded-xl border border-gray-100 text-sm text-gray-400">Loading…</div>
          ) : filteredTransactions.length === 0 ? (
            <div className="p-4 bg-white rounded-xl border border-gray-100 text-sm text-gray-400">No transactions found.</div>
          ) : filteredTransactions.map((t) => (
            <article key={t.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-gray-400">{new Date(t.date).toLocaleString()}</p>
                  <h3 className="text-sm font-semibold text-gray-900 truncate mt-0.5">{t.customer?.name || '—'}</h3>
                  <p className="text-xs text-gray-400 truncate">{t.transactionId}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-gray-900">₹{t.amount.toFixed(2)}</p>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${
                    t.status === 'paid' || t.status === 'completed' ? 'bg-green-100 text-green-700'
                    : t.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>{t.status}</span>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => handleViewDetails(t)} className="flex-1 py-2 bg-teal-600 text-white text-xs font-medium rounded-lg">View Details</button>
                <button onClick={() => handleDownloadReceipt(t)} className="px-3 py-2 bg-gray-100 text-xs rounded-lg border border-gray-200">Receipt</button>
                <button onClick={() => handleProcessRefund(t)} className="px-3 py-2 bg-red-50 text-xs rounded-lg border border-red-200 text-red-700">Refund</button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── Modals ── */}
      <TransactionDetailsModal
        isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)}
        transaction={selectedTransaction} onDownloadReceipt={handleDownloadReceipt} onProcessRefund={handleProcessRefund}
      />

      <Modal open={downloadModalOpen} title="Export Transactions" onClose={() => setDownloadModalOpen(false)}
        footer={
          <>
            <button onClick={() => setDownloadModalOpen(false)} className="px-4 py-2 border text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={confirmExport} disabled={exporting} className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg disabled:opacity-50">
              {exporting ? 'Exporting…' : `Export ${exportFormat.toUpperCase()}`}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-stone-600">Export <strong>filtered</strong> transactions (current search &amp; filters).</p>
          <div className="flex gap-3">
            {['csv','json'].map(fmt => (
              <label key={fmt} className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer text-sm font-medium transition-colors ${exportFormat === fmt ? 'bg-teal-50 border-teal-400 text-teal-700' : 'hover:bg-gray-50'}`}>
                <input type="radio" name="exportFormat" value={fmt} checked={exportFormat === fmt} onChange={() => setExportFormat(fmt)} className="accent-teal-600" />
                {fmt.toUpperCase()}
              </label>
            ))}
          </div>
          <p className="text-xs text-stone-400">Only the currently visible records will be included.</p>
        </div>
      </Modal>
    </main>
  );
};

export default TransactionsPage;